import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { authyEndpoint } from "../constants";

const Recruiters = () => {
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Mock navigation function - replace with actual router navigation
  const navigate = (path) => console.log(`Navigate to: ${path}`);
  
  // Mock token - replace with actual token retrieval
  const token = localStorage.getItem('token');
  
  // API endpoint for recruiters - replace with actual endpoint
  const recruitersEndpoint = authyEndpoint+'recruiters/';

  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        setLoading(true);
        
        // Check if token exists
        if (!token) {
          setError("No authentication token found");
          return;
        }
        
        const response = await axios.get(recruitersEndpoint, {
          headers: { 'Authorization': `Token ${token}` },
        });
        
        // Add safety check for response data
        const data = response.data;
        console.log('API Response:', data); // Debug log
        
        // Handle different response structures
        if (Array.isArray(data)) {
          setRecruiters(data);
        } else if (data && Array.isArray(data.results)) {
          // Handle paginated responses
          setRecruiters(data.results);
        } else if (data && Array.isArray(data.recruiters)) {
          // Handle nested responses
          setRecruiters(data.recruiters);
        } else {
          console.error('Unexpected response structure:', data);
          setError("Unexpected data format received from server");
          setRecruiters([]); // Ensure it's always an array
        }
      } catch (err) {
        if (err.response?.status === 401) {
          setError("Authentication failed. Please login again.");
          // Redirect to login or refresh token
        } else {
          setError("Failed to load recruiters.");
        }
        console.error('Fetch error:', err);
        setRecruiters([]); // Ensure it's always an array on error
      } finally {
        setLoading(false);
      }
    };

    fetchRecruiters();
  }, [token]);

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'online':
        return 'bg-green-100 text-green-600';
      case 'busy':
        return 'bg-yellow-100 text-yellow-600';
      case 'offline':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatExperience = (years) => {
    if (!years) return 'N/A';
    return `${years} years`;
  };

  const formatAcceptanceRate = (rate) => {
    if (rate === null || rate === undefined) return 'N/A';
    return `${rate.toFixed(1)}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="lg:p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Recruiters</h1>
          <p className="mt-1 text-sm text-gray-600">
            Here is a complete list of all Recruiters.
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500">Loading recruiters...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lg:p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Recruiters</h1>
          <p className="mt-1 text-sm text-gray-600">
            Here is a complete list of all Recruiters.
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:p-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Recruiters</h1>
            <p className="mt-1 text-sm text-gray-600">
              Here is a complete list of all Recruiters.
            </p>
          </div>
          <button
            onClick={() => navigate("/manager/recruiters/usermanagement")}
            className="px-4 py-2 flex items-center cursor-pointer gap-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add Recruiter
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 mt-10">
        <div className="pt-3 px-5">
          <h3 className="text-sm font-semibold text-gray-900">
            Total Recruiters
          </h3>
        </div>
        <div className="flex gap-3 items-end py-4 px-5 border-b border-gray-200 rounded-t-md">
          <h3 className="text-2xl text-gray-900 font-semibold">{recruiters.length}</h3>
          <div className="flex items-center gap-1 text-sm text-emerald-600">
            <span>↑ 8.6%</span>
            <span className="text-gray-600 ml-1">vs last 30 days</span>
          </div>
        </div>

        <div className="hidden md:grid px-5 py-3 grid-cols-8 border-b border-gray-200 text-sm font-semibold text-gray-500">
          <div className="truncate">Recruiter Name</div>
          <div className="truncate">Email</div>
          <div>Jobs Managed</div>
          <div>Avg. Time-to-Fill</div>
          <div>Engagement Rate</div>
          <div>Offer Acceptance Rate</div>
          <div>AI Score</div>
          <div>Status</div>
        </div>

        {recruiters.map((recruiter) => (
          <div key={recruiter.id} className="border-b border-gray-200">
            <div className="hidden md:grid grid-cols-8 items-center px-5 py-4">
              <div className="flex items-center gap-3 truncate">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                  {recruiter.picture ? (
                    <img
                      src={recruiter.picture}
                      alt={`${recruiter.first_name} ${recruiter.last_name}`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-sm text-gray-400">
                      {recruiter.first_name?.[0]}{recruiter.last_name?.[0]}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {recruiter.first_name} {recruiter.last_name}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-700 truncate">{recruiter.user?.email || 'N/A'}</p>
              <p className="text-sm text-gray-700">{formatExperience(recruiter.years_of_experience)}</p>
              <p className="text-sm text-gray-700">{recruiter.num_applicants || 0}</p>
              <p className="text-sm text-gray-700">{recruiter.num_workers || 0}</p>
              <p className="text-sm text-gray-700">{formatAcceptanceRate(recruiter.offer_acceptance_rate)}</p>
              <p className="text-sm text-gray-700">{formatDate(recruiter.most_recent_applicant_date)}</p>
              <span
                className={`text-xs px-2 py-1 rounded-md text-center ${getStatusBadgeColor(recruiter.status)}`}
              >
                {recruiter.status || 'Unknown'}
              </span>
            </div>

            <div className="md:hidden px-5 py-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                  {recruiter.picture ? (
                    <img
                      src={recruiter.picture}
                      alt={`${recruiter.first_name} ${recruiter.last_name}`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-sm text-gray-400">
                      {recruiter.first_name?.[0]}{recruiter.last_name?.[0]}
                    </span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{recruiter.first_name} {recruiter.last_name}</p>
                  <p className="text-sm text-gray-500">{recruiter.user?.email || 'N/A'}</p>
                </div>
              </div>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Experience:</strong> {formatExperience(recruiter.years_of_experience)}</p>
                <p><strong>Applicants:</strong> {recruiter.num_applicants || 0}</p>
                <p><strong>Workers:</strong> {recruiter.num_workers || 0}</p>
                <p><strong>Acceptance Rate:</strong> {formatAcceptanceRate(recruiter.offer_acceptance_rate)}</p>
                <p><strong>Recent Activity:</strong> {formatDate(recruiter.most_recent_applicant_date)}</p>
                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={`ml-2 text-xs px-2 py-1 rounded-md ${getStatusBadgeColor(recruiter.status)}`}
                  >
                    {recruiter.status || 'Unknown'}
                  </span>
                </p>
              </div>
            </div>
          </div>
        ))}

        <div className="px-5 py-4 flex items-center justify-between bg-gray-50">
          <p className="text-sm font-semibold text-gray-900">
            Page <span>1</span> of <span>1</span>
          </p>
          <div className="flex gap-3 items-center">
            <button className="text-sm bg-white border border-gray-300 text-gray-900 py-2 px-3 rounded-md cursor-pointer">
              Previous
            </button>
            <button className="text-sm bg-white text-gray-900 py-2 px-3 border border-gray-300 rounded-md cursor-pointer">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Recruiters;